import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';

interface ThemeVisitRow {
  id: number;
  boutique_id: number | null;
  session_id: string | null;
  page_key: string | null;
  path: string | null;
  ip: string | null;
  user_agent: string | null;
  referrer: string | null;
  country: string | null;
  event: string | null;
  duration_seconds: number | null;
  created_at: string;
}

interface ThemeTrafficModalProps {
  onClose: () => void;
}

function formatDuration(seconds: number): string {
  if (!seconds || seconds < 1) return '—';
  if (seconds < 60) return `${seconds} s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m} min ${s.toString().padStart(2, '0')} s`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

/**
 * Panneau de suivi du trafic des pages de thème (sites publics) :
 * compteur de visites par page, temps passé, IP des visiteurs et
 * comportement (chemin, referrer, session).
 */
export default function ThemeTrafficModal({ onClose }: ThemeTrafficModalProps) {
  const [rows, setRows] = useState<ThemeVisitRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const { data, error: err } = await supabase
          .from('theme_page_visits')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(1000);
        if (err) throw new Error(err.message);
        if (active) setRows((data as ThemeVisitRow[]) || []);
      } catch (e) {
        if (active) setError(e instanceof Error ? e.message : 'Erreur de chargement');
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => { active = false; };
  }, []);

  const stats = useMemo(() => {
    const views = rows.filter((r) => (r.event || 'view') === 'view');
    const uniqueSessions = new Set(rows.map((r) => r.session_id).filter(Boolean));
    const uniqueIps = new Set(rows.map((r) => r.ip).filter(Boolean));

    const perPage = new Map<string, { views: number; totalDuration: number; durationSamples: number; visitors: Set<string> }>();
    for (const r of rows) {
      const key = r.page_key || '(inconnue)';
      const entry = perPage.get(key) || { views: 0, totalDuration: 0, durationSamples: 0, visitors: new Set<string>() };
      if ((r.event || 'view') === 'view') entry.views += 1;
      if (r.event === 'leave' || r.event === 'ping') {
        const d = Number(r.duration_seconds) || 0;
        if (d > 0) { entry.totalDuration += d; entry.durationSamples += 1; }
      }
      if (r.session_id) entry.visitors.add(r.session_id);
      perPage.set(key, entry);
    }

    return {
      totalViews: views.length,
      uniqueVisitors: uniqueSessions.size,
      uniqueIps: uniqueIps.size,
      pages: Array.from(perPage.entries())
        .map(([key, v]) => ({
          key,
          views: v.views,
          visitors: v.visitors.size,
          avgDuration: v.durationSamples > 0 ? Math.round(v.totalDuration / v.durationSamples) : 0,
        }))
        .sort((a, b) => b.views - a.views),
    };
  }, [rows]);

  const recent = rows.slice(0, 60);

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-[70]" onClick={onClose} />
      <div className="fixed inset-0 z-[71] flex items-center justify-center p-3 sm:p-4">
        <div className="w-full max-w-4xl h-[88vh] bg-background-50 rounded-lg border border-background-200/70 flex flex-col overflow-hidden shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-background-200/70 flex-shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <i className="ri-bar-chart-box-line text-accent-600"></i>
              <h3 className="text-sm font-semibold text-foreground-950 truncate">Trafic des sites (thèmes)</h3>
            </div>
            <button
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-foreground-500"></i>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-5">
            {loading ? (
              <div className="h-full flex items-center justify-center">
                <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <i className="ri-error-warning-line text-3xl text-red-500 mb-2 block"></i>
                <p className="text-sm text-foreground-600">{error}</p>
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center py-16">
                <i className="ri-line-chart-line text-4xl text-foreground-300 mb-3 block"></i>
                <p className="text-sm text-foreground-600 font-medium">Aucune visite enregistrée</p>
                <p className="text-xs text-foreground-400 mt-1 max-w-md mx-auto">
                  Les visites des sites publics des marchands (compteur de vues, temps passé, IP) apparaîtront ici dès
                  qu&apos;un visiteur navigue.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Overview */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="rounded-lg border border-background-200/70 bg-background-100/50 p-3.5">
                    <div className="flex items-center gap-2 text-foreground-500 mb-1">
                      <i className="ri-eye-line text-sm"></i>
                      <span className="text-[11px] font-medium uppercase tracking-wider">Vues de pages</span>
                    </div>
                    <p className="text-2xl font-bold font-heading text-foreground-950">{stats.totalViews.toLocaleString()}</p>
                  </div>
                  <div className="rounded-lg border border-background-200/70 bg-background-100/50 p-3.5">
                    <div className="flex items-center gap-2 text-foreground-500 mb-1">
                      <i className="ri-user-3-line text-sm"></i>
                      <span className="text-[11px] font-medium uppercase tracking-wider">Visiteurs uniques</span>
                    </div>
                    <p className="text-2xl font-bold font-heading text-foreground-950">{stats.uniqueVisitors.toLocaleString()}</p>
                  </div>
                  <div className="rounded-lg border border-background-200/70 bg-background-100/50 p-3.5">
                    <div className="flex items-center gap-2 text-foreground-500 mb-1">
                      <i className="ri-global-line text-sm"></i>
                      <span className="text-[11px] font-medium uppercase tracking-wider">IP distinctes</span>
                    </div>
                    <p className="text-2xl font-bold font-heading text-foreground-950">{stats.uniqueIps.toLocaleString()}</p>
                  </div>
                </div>

                {/* Per page */}
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-foreground-700 mb-2">Compteur par page</h4>
                  <div className="overflow-x-auto rounded-lg border border-background-200/70">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-background-100 text-foreground-600">
                        <tr>
                          <th className="px-3 py-2 font-semibold">Page</th>
                          <th className="px-3 py-2 font-semibold text-right">Vues</th>
                          <th className="px-3 py-2 font-semibold text-right">Visiteurs</th>
                          <th className="px-3 py-2 font-semibold text-right">Temps moyen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.pages.map((p) => (
                          <tr key={p.key} className="border-t border-background-200/70">
                            <td className="px-3 py-2 font-mono text-primary-700">{p.key}</td>
                            <td className="px-3 py-2 text-right text-foreground-800 font-medium">{p.views}</td>
                            <td className="px-3 py-2 text-right text-foreground-600">{p.visitors}</td>
                            <td className="px-3 py-2 text-right text-foreground-600">{formatDuration(p.avgDuration)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Recent visits */}
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-foreground-700 mb-2">
                    Comportement des visiteurs (IP)
                  </h4>
                  <div className="overflow-x-auto rounded-lg border border-background-200/70">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-background-100 text-foreground-600">
                        <tr>
                          <th className="px-3 py-2 font-semibold">Date</th>
                          <th className="px-3 py-2 font-semibold">IP</th>
                          <th className="px-3 py-2 font-semibold">Page</th>
                          <th className="px-3 py-2 font-semibold">Événement</th>
                          <th className="px-3 py-2 font-semibold text-right">Temps</th>
                          <th className="px-3 py-2 font-semibold">Référent</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recent.map((r) => (
                          <tr key={r.id} className="border-t border-background-200/70">
                            <td className="px-3 py-2 text-foreground-500 whitespace-nowrap">{formatDate(r.created_at)}</td>
                            <td className="px-3 py-2 font-mono text-foreground-700">{r.ip || '—'}</td>
                            <td className="px-3 py-2 font-mono text-foreground-600">{r.page_key || r.path || '—'}</td>
                            <td className="px-3 py-2">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${r.event === 'leave' ? 'bg-accent-100 text-accent-700' : r.event === 'ping' ? 'bg-secondary-100 text-secondary-700' : 'bg-background-100 text-foreground-600'}`}>
                                {r.event || 'view'}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right text-foreground-600">{r.duration_seconds ? formatDuration(r.duration_seconds) : '—'}</td>
                            <td className="px-3 py-2 text-foreground-400 truncate max-w-[160px]">{r.referrer || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}